// 22.01.19 - Specifies the actions used by the form_wrapper and view cases.
import {
  all,
  call,
  put,
  takeLatest,
  takeEvery,
  select,
  delay,
} from "redux-saga/effects"; // Standard Saga functions.
import { ActionTypes } from "../constants/actions";
import { SEPARATOR } from "../constants/data";
import {
  getAllCasesByUserUtility,
  apiExportClaim,
  apiExportComplaint,
  apiExportIncidentReport,
} from "../effects/api/home"; // getAllCasesUtility,
import {
  countriesReceived,
  issueTypesReceived,
  claimTypesReceived,
  claimStatusReceived,
  grantTypesReceived,
  solutionUsersReceived,
} from "src/actions/detail";
import {
  getRequest,
  getRequestSync,
  IServerResponse,
} from "src/effects/api/common";
import {
  GET_CLAIMTYPES_ALL,
  GET_CLAIMSTATUS_ALL,
  GET_GRANTTYPES_ALL,
  GET_ISSUETYPES_ALL,
  GET_COUNTRY_ALL,
  GET_USERDETAILS,
  IUserDetail,
  ICase,
  SOLUTION_OWNER_TEAM,
  GET_LOGGED_USERDETAILS,
  CaseFormType,
} from "src/constants/api";
import {
  userDataReceived,
  actionSuccessGetGridData,
  actionShowNoDataMessage,
} from "src/actions/home";
import { initialize } from "redux-form"; // Redux form action we use to initialise specific fields.
import { IRootState } from "src/reducers";
import {
  actionSetAttachmentData,
  actionSetAttachmentDataShipment,
  actionSetAttachmentDataShipmentInventory,
  actionSetAttachmentDataQmu,
  actionSetAttachmentDataInventory,
  actionSetAttachmentDataQmuOer,
  fetchCaseSucceeded,
  actionIsCheckOutByDiffUser,
  actionIsCurrentUserSO,
  actionIsCurrentUserST,
  isLockForEdit,
} from "src/actions/detail";
import {
  capitalizeString,
  getLoggedInCurrentUserDetails,
  getStringAfterSubstring,
} from "../utils";
import { CURRENCY_LIST } from "../constants/data";
import { setLoaderVisibility, openToaster } from "src/actions/common";
import { API_ERROR_MESSAGE } from "src/constants/error";

// Initialise the new case with needed fields. Called by the function below.
function emptyCase(userName: string, caseFormType: CaseFormType): Partial<ICase> {
  const today = new Date().toISOString();
  const defaultCurrency = CURRENCY_LIST[0].value;
  const newCase = {
    created: today.split(".")[0],
    createdBy: userName,
    caseFormType,
    caseNo: "00-0000",
    qmus: [
      {
        validatedAsComplaint: 2,
        recurrent: 2,
      }
    ],
    shipments: [
      {
        expectedLossCurrency: caseFormType === CaseFormType.WarehouseNonConformity ? '' : defaultCurrency,
        actualInsuredValuesCurrency: defaultCurrency,
        premiumCurrency: defaultCurrency,
        totalClaimsValuesSuppliesCurrency: defaultCurrency,
        claimsValueFreightCurrency: defaultCurrency,
        upliftCurrency: defaultCurrency,
        deductibleUnderThePolicyCurrency: defaultCurrency,
        totalValueOfLossCurrency: defaultCurrency,
      },
    ],
  };
  // @ts-ignore
  return newCase;
}

// These * Saga generator functions. We can return a stream of actions/values with these.
function* watchNewCase() {
  yield takeLatest(ActionTypes.DETAIL.NEW_CASE, initializeNewCase);
}

function* watchExportClaim() {
  yield takeLatest(ActionTypes.HOME.EXPORT_CLAIM, fnExportClaim);
}

function* watchExportComplaint() {
  yield takeLatest(ActionTypes.HOME.EXPORT_COMPLAINT, fnExportComplaint);
}

function* watchExportIncidentReport() {
  yield takeLatest(
    ActionTypes.HOME.EXPORT_INCIDENT_REPORT,
    fnExportIncidentReport
  );
}

function* initializeNewCase() {
  const state: IRootState = yield select();
  if (state.home.currentUser) {
    // @ts-ignore
    yield put(fetchCaseSucceeded({}));
    let { name, firstName, lastName, emails } = getLoggedInCurrentUserDetails(
      state.home.currentUser
    );
    let username = state.home.currentUser.userName;
    if (username.includes(":")) {
      const modifiedUsername = getStringAfterSubstring(username, ":").trim();
      name = `${capitalizeString(name)} (${modifiedUsername})`;
    } else {
      username = username.trim();
      name = `${capitalizeString(name)} (${username})`;
    }
    // Reset some flag variables
    yield put(actionIsCheckOutByDiffUser(false));
    yield put(actionIsCurrentUserSO(false));
    yield put(actionIsCurrentUserST(false));
    yield put(isLockForEdit(false));

    // Initialize new case
    const caseFormType = state.router.location.pathname.includes("incident-mainform")
      ? CaseFormType.Incident
      : state.router.location.pathname.includes("wh-nonconformity-mainform")
        ? CaseFormType.WarehouseNonConformity
        : CaseFormType.Standard;
    yield put(
      initialize("mainForm", emptyCase(name, caseFormType), {
        updateUnregisteredFields: true,
      })
    );
    // Empty the file attachment fields
    yield put(actionSetAttachmentData([]));
    yield put(actionSetAttachmentDataShipment([]));
    yield put(actionSetAttachmentDataShipmentInventory([]));
    yield put(actionSetAttachmentDataQmu([]));
    yield put(actionSetAttachmentDataInventory([]));
    yield put(actionSetAttachmentDataQmuOer([]));
  }
}

/*function* watchGetAllCases() {
  // takeLatest ensures that we do not call getAllCases multiple times if
  // the GET_ALL_CASES event is sent multiple times
  yield takeLatest(ActionTypes.HOME.GET_ALL_CASES, getAllCases);
}
*/
// This Saga listens for FetchUserData then calls getUserData.
function* watchFetchUserData() {
  yield takeLatest(ActionTypes.HOME.FETCH_USER_DATA, getUserData);
}

// Saga receives the data and then put userDataReceived
function* getUserData(action: any) {
  const response: Required<IServerResponse<
    IUserDetail
  >> = yield call(getRequest, [GET_USERDETAILS, action.payload]);
  if (response.success) {
    yield put(userDataReceived(response.data));
  } else {
    // TODO: Add generic error handling
  }
}

function* watchFetchLoggedUserData() {
  yield takeLatest(ActionTypes.HOME.FETCH_LOGGED_USER_DATA, getLoggedUserData);
}

// Saga receives the data and then put userDataReceived
function* getLoggedUserData(action: any) {
  const response: Required<IServerResponse<
    IUserDetail
  >> = yield call(getRequest, [GET_LOGGED_USERDETAILS]);
  if (response.success) {
    yield put({
      type: ActionTypes.HOME.USER_DATA_RECEIVED,
      payload: response.data,
    });
    yield put(userDataReceived(response.data));
  } else {
    // TODO: Add generic error handling
  }
}

function* getInitialData(): any {
  // Fetch all the initial data we need in parallel
  // `all` is what makes it happen in parallel
  const countries = yield call(getRequestSync, [GET_COUNTRY_ALL]);
  if (countries && countries.success) {
    yield put(countriesReceived(countries.data));
  }

  yield delay(500);

  const [
    //countries,
    issueTypes,
    claimTypes,
    claimStatus,
    grantTypes,
    solutionUsers,
  ] = yield all([
    //call(getRequestSync, [GET_COUNTRY_ALL]),
    call(getRequestSync, [GET_ISSUETYPES_ALL]),
    call(getRequestSync, [GET_CLAIMTYPES_ALL]),
    call(getRequestSync, [GET_CLAIMSTATUS_ALL]),
    call(getRequestSync, [GET_GRANTTYPES_ALL]),
    call(getRequestSync, [SOLUTION_OWNER_TEAM]),
  ]);

  // if (countries.success) {
  //   yield put(countriesReceived(countries.data));
  // }
  if (issueTypes.success) {
    yield put(issueTypesReceived(issueTypes.data));
  }
  if (claimTypes.success) {
    yield put(claimTypesReceived(claimTypes.data));
  }
  if (claimStatus.success) {
    yield put(claimStatusReceived(claimStatus.data));
  }
  if (grantTypes.success) {
    yield put(grantTypesReceived(grantTypes.data));
  }
  if (solutionUsers.success) {
    yield put(solutionUsersReceived(solutionUsers.data));
  }
}

function* watchFetchInitialData() {
  try {
    yield takeEvery(ActionTypes.HOME.FETCH_INITIAL_DATA, getInitialData);
  } catch (e) {
    console.log("Error during initial data fetch: ", e);
  }
}

/*function* getAllCases() {
  try {
    const response = yield call(getAllCasesUtility);
    // dispatch the event that we handle in our reducers/home.tsx that we use to update
    // the state with the data we
    yield put({
      type: ActionTypes.HOME.SUCCESS_GET_ALL_CASES,
      payload: response.data
    });
  } catch (err) {
    console.log("Error:", err);
  }
}*/

/// This Saga listens for FetchUserData then calls getUserData.
function* watchGetAllCaseDataByUser() {
  yield takeLatest(ActionTypes.HOME.GET_ALL_CASES_BY_USER, getAllCasesByUser);
}

function* watchGetGridData() {
  yield takeLatest(ActionTypes.HOME.GET_GRID_DATA, getGridData);
}

function* getAllCasesByUser(action: any) {
  try {
    const response = yield call(getAllCasesByUserUtility, action.payload);
    const updatedCaseData = response.data.map((item: any) => {
      if (item.insurancePolicyYear) {
        item.insurancePolicyYear = new Date(item.insurancePolicyYear)
          .getFullYear()
          .toString();
      }

      if (item.qmus[0]) {
        if (typeof item.qmus[0].oer === "boolean") {
          item.qmus[0].oer = Number(item.qmus[0].oer);
        }

        if (typeof item.qmus[0].recurrent === "boolean") {
          item.qmus[0].recurrent = Number(item.qmus[0].recurrent);
        }
      }

      if (item.expectedLossValue) {
        const currentData = item.expectedLossValue.split(SEPARATOR);
        item.expectedLossValue = currentData[1];
        item.expectedLossCurrency = currentData[0];
      }

      if (item.shipments[0]) {
        if (item.shipments[0].actualInsuredValues) {
          const freightCurrencyData = item.shipments[0].actualInsuredValues.split(
            SEPARATOR
          );
          item.shipments[0].actualInsuredValues = freightCurrencyData[1];
          item.shipments[0].actualInsuredValuesCurrency =
            freightCurrencyData[0];
        }

        if (item.shipments[0].premium) {
          const premiumCurrencyData = item.shipments[0].premium.split(
            SEPARATOR
          );
          item.shipments[0].premium = premiumCurrencyData[1];
          item.shipments[0].premiumCurrency = premiumCurrencyData[0];
        }

        if (item.shipments[0].totalClaimsValuesSupplies) {
          const premiumCurrencyData = item.shipments[0].totalClaimsValuesSupplies.split(
            SEPARATOR
          );
          item.shipments[0].totalClaimsValuesSupplies = premiumCurrencyData[1];
          item.shipments[0].totalClaimsValuesSuppliesCurrency =
            premiumCurrencyData[0];
        }

        if (item.shipments[0].claimsValueFreight) {
          const premiumCurrencyData = item.shipments[0].claimsValueFreight.split(
            SEPARATOR
          );
          item.shipments[0].claimsValueFreight = premiumCurrencyData[1];
          item.shipments[0].claimsValueFreightCurrency = premiumCurrencyData[0];
        }

        if (item.shipments[0].uplift) {
          const premiumCurrencyData = item.shipments[0].uplift.split(SEPARATOR);
          item.shipments[0].uplift = premiumCurrencyData[1];
          item.shipments[0].upliftCurrency = premiumCurrencyData[0];
        }

        if (item.shipments[0].deductibleUnderThePolicy) {
          const premiumCurrencyData = item.shipments[0].deductibleUnderThePolicy.split(
            SEPARATOR
          );
          item.shipments[0].deductibleUnderThePolicy = premiumCurrencyData[1];
          item.shipments[0].deductibleUnderThePolicyCurrency =
            premiumCurrencyData[0];
        }

        if (item.shipments[0].totalValueOfLoss) {
          const premiumCurrencyData = item.shipments[0].totalValueOfLoss.split(
            SEPARATOR
          );
          item.shipments[0].totalValueOfLoss = premiumCurrencyData[1];
          item.shipments[0].totalValueOfLossCurrency = premiumCurrencyData[0];
        }
      }

      return item;
    });
    yield put({
      type: ActionTypes.HOME.SUCCESS_GET_ALL_CASES,
      payload: updatedCaseData,
    });
  } catch (err) {
    console.log("Error:", err);
  }
}

function* getGridData(action: any) {
  try {
    const response = yield call(getAllCasesByUserUtility, action.payload);
    const updatedCaseData = response.data;
    if (updatedCaseData.length) {
      // Modify the data before sending it to UI
      const modifiedData = updatedCaseData.map((item: any) => {
        // Get cateogry value
        const arrCategory = (window as any).categories.filter(
          (value: any) => value.id === item.category
        );
        const category = arrCategory.length ? arrCategory[0].name : "";
        return {
          ...item,
          category,
        };
      });

      yield put(actionSuccessGetGridData(modifiedData));
      yield put(actionShowNoDataMessage(false));
    } else {
      yield put(actionShowNoDataMessage(true));
    }
  } catch (err) {
    console.log("Error:", err);
  }
}

// Export & download the COCA shipment data in excel
function* fnExportClaim() {
  try {
    yield put(setLoaderVisibility(true));
    const response: any = yield call(apiExportClaim);
    if (response.status === 200) {
      setTimeout(() => {
        const file: any = new Blob([response.data], {
          type: "application/octet-stream",
        });
        const a: any = document.createElement("a");
        a.className = "link-download";
        document.body.appendChild(a);
        const url: any = window.URL.createObjectURL(file);
        a.href = url;
        a.download = "ClaimDetails.xlsx";
        a.click();
        document.body.removeChild(a);
      }, 0);
      yield put(setLoaderVisibility(false));
    } else {
      yield put(setLoaderVisibility(false));
      yield put(openToaster(API_ERROR_MESSAGE, false));
    }
  } catch (err) {
    console.log(err);
    yield put(setLoaderVisibility(false));
    yield put(openToaster(API_ERROR_MESSAGE, false));
  }
}

// Export & download the COCA shipment data in excel
function* fnExportComplaint() {
  try {
    yield put(setLoaderVisibility(true));
    const response: any = yield call(apiExportComplaint);
    if (response.status === 200) {
      setTimeout(() => {
        const file: any = new Blob([response.data], {
          type: "application/octet-stream",
        });
        const a: any = document.createElement("a");
        a.className = "link-download";
        document.body.appendChild(a);
        const url: any = window.URL.createObjectURL(file);
        a.href = url;
        a.download = "ComplaintDetails.xlsx";
        a.click();
        document.body.removeChild(a);
      }, 0);
      yield put(setLoaderVisibility(false));
    } else {
      yield put(setLoaderVisibility(false));
      yield put(openToaster(API_ERROR_MESSAGE, false));
    }
  } catch (err) {
    console.log(err);
    yield put(setLoaderVisibility(false));
    yield put(openToaster(API_ERROR_MESSAGE, false));
  }
}

// Export & download the COCA shipment data in excel
function* fnExportIncidentReport() {
  try {
    yield put(setLoaderVisibility(true));
    const response: any = yield call(apiExportIncidentReport);
    if (response.status === 200) {
      setTimeout(() => {
        const file: any = new Blob([response.data], {
          type: "application/octet-stream",
        });
        const a: any = document.createElement("a");
        a.className = "link-download";
        document.body.appendChild(a);
        const url: any = window.URL.createObjectURL(file);
        a.href = url;
        a.download = "IncidentReport.xlsx";
        a.click();
        document.body.removeChild(a);
      }, 0);
      yield put(setLoaderVisibility(false));
    } else {
      yield put(setLoaderVisibility(false));
      yield put(openToaster(API_ERROR_MESSAGE, false));
    }
  } catch (err) {
    console.log(err);
    yield put(setLoaderVisibility(false));
    yield put(openToaster(API_ERROR_MESSAGE, false));
  }
}

// This is one of our main sagas, and it is responsible for watching for and reacting to
// - The GET_CASE_ALL event
// - Initial data fetch event
// - The user data fetch event
// - and the new case event
// The events come from redux
// sagas let you wait for certain events and then trigger other events/side-effects(network, etc)
// when those events happen
// The all function ensures that these Sagas can run in parallel.
export default function* homeSaga() {
  yield all([
    // watchGetAllCases(),
    watchGetAllCaseDataByUser(),
    watchFetchInitialData(),
    watchFetchUserData(),
    watchNewCase(),
    watchGetGridData(),
    watchExportClaim(),
    watchExportComplaint(),
    watchExportIncidentReport(),
    watchFetchLoggedUserData(),
  ]);
}
